import { useContext, useState, useMemo, useCallback } from "react";
import AuthContext from "src/app/contexts/auth-context";
import { UserPermissions } from "src/app/providers/permissions/permissions";
import {
  AdminRoleAction,
  canPerformAdminRoleAction,
} from "src/app/providers/permissions/admin-role-policy";
import {
  getPrimaryMHCRoleLabel,
  MHCRoles,
} from "src/app/providers/permissions/mhc-roles";

const readStoredList = (key: string): string[] =>
  JSON.parse(localStorage.getItem(key) || "[]");

export function usePermission() {
  const { mhcPermissions, mhcRoles } = useContext(AuthContext);
  // localStorage is only a fallback until the auth provider has fetched fresh data.
  const [storedPermissions] = useState(() =>
    readStoredList("userMHCPermissions"),
  );
  const [storedRoles] = useState(() => readStoredList("userMHCRoles"));

  const userPermissions = mhcPermissions ?? storedPermissions;
  const userRoles = mhcRoles ?? storedRoles;

  function hasPermission(to: string | string[]) {
    if (Array.isArray(to)) {
      return to.some((p) => userPermissions.includes(p));
    }
    return userPermissions.includes(to);
  }

  // Check if user has ONLY the Karibu Utility access permission (no other permissions)
  const hasOnlyKaribuUtilityAccess = useMemo(() => {
    if (userPermissions.length === 0) return false;

    const karibuPermissions = new Set<string>([
      UserPermissions.KARIBU_UTILITY.ACCESS,
      UserPermissions.KARIBU_UTILITY.REPROCESS,
      UserPermissions.PAYMENTS.CREATE,
      UserPermissions.PAYMENTS.VIEW,
      UserPermissions.PAYMENTS.EDIT,
    ]);

    // Check if all user permissions are Karibu Utility permissions
    const hasOnlyKaribuPermissions = userPermissions.every((p) =>
      karibuPermissions.has(p),
    );

    // Must have the ACCESS permission
    const hasAccessPermission = userPermissions.includes(
      UserPermissions.KARIBU_UTILITY.ACCESS,
    );

    return hasOnlyKaribuPermissions && hasAccessPermission;
  }, [userPermissions]);

  // Check if user has Karibu Utility access (but may have other permissions too)
  const hasKaribuUtilityAccess = useMemo(() => {
    return userPermissions.includes(UserPermissions.KARIBU_UTILITY.ACCESS);
  }, [userPermissions]);

  // Check if user can reprocess BODs
  const canReprocessBod = useMemo(() => {
    return userPermissions.includes(UserPermissions.KARIBU_UTILITY.REPROCESS);
  }, [userPermissions]);

  const canPerformRoleAction = useCallback(
    (action: AdminRoleAction) => {
      return canPerformAdminRoleAction(userRoles, action);
    },
    [userRoles],
  );

  const primaryRoleLabel = useMemo(
    () => getPrimaryMHCRoleLabel(userRoles),
    [userRoles],
  );

  // Temporary: remove once MHC grants Sales Officers the PAYMENT_TRANSCRIPTION permissions.
  const hasSalesOfficerTranscriptionAccess = useMemo(
    () => userRoles.includes(MHCRoles.SALES_OFFICER),
    [userRoles],
  );

  return {
    userPermissions,
    hasPermission,
    hasSalesOfficerTranscriptionAccess,
    canPerformRoleAction,
    primaryRoleLabel,
    hasOnlyKaribuUtilityAccess,
    hasKaribuUtilityAccess,
    canReprocessBod,
  };
}
